import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { usePersona, trackMixpanelEvent } from '@belong/common';
import { IconSpinner } from '@belong/icons';
import { IdentityVerificationBanner } from '@belong/ui';
import { StepFormLayout } from 'accounts/components/step-form-layout';
import { UserIdentityBannerStep } from 'accounts/components/user-identity-banner-step';
import { STEP_GATE_NAMES } from 'accounts/constants/steps';
import { USER_IDENTITY_STRINGS } from 'accounts/constants/strings/user-identity.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import { isStepCompleted, isStepUnHidden } from 'accounts/utils/flow-steps';
import { useUserIdentityVerification } from 'hooks/useUserIdentityVerification';
import { selectUser } from 'store/redux/user/selectors';

export function UserIdentity() {
  const {
    currentStepFormData,
    loading,
    setLoading,
    stepsByGate,
    handleSaveAndNext,
    steps,
    properties,
    currentStep,
    propertyId,
    unitId,
  } = useSetupFlowStep();
  const user = useSelector(selectUser);

  const eventMetadata = {
    source: 'App' as const,
    flowType: 'Invitation' as const,
    flowSubType: 'Onboarding' as const,
    stepEnum: 'user-identity',
    stepDisplayName: 'User Identity',
    homeId: unitId,
  };

  const trackError = (error: any, context: any) => {
    const errorData = {
      context,
      error: {
        message: error?.message,
        status: error?.status || error?.code,
        response: error?.response?.data || error?.response,
        stack: error?.stack,
      },
      componentState: {
        loading,
        hasProperties: !!properties,
        hasSteps: !!steps,
        hasCurrentStep: !!currentStep,
        hasCurrentStepFormData: !!currentStepFormData,
        propertyId,
        unitId,
        stepId: currentStep?.uniqueId,
      },
    };

    trackMixpanelEvent('flow-error', {
      ...eventMetadata,
      parsedError: errorData,
    });
  };

  const {
    hasFetchedIdentityVerification,
    isLoadingIdentityVerification,
    identityVerification,
    refetchIdentityVerification,
  } = useUserIdentityVerification(user?.id, true);
  const {
    isLoadingPersona,
    isVerifying,
    isWaitingSafeDelay,
    startVerification,
  } = usePersona({});
  // Track component state changes
  useEffect(() => {
    const stateInfo = {
      loading,
      hasProperties: !!properties,
      hasSteps: !!steps,
      hasCurrentStep: !!currentStep,
      hasCurrentStepFormData: !!currentStepFormData,
      currentStepStatus: currentStep?.status,
      identityVerificationStatus: identityVerification?.status,
    };

    trackMixpanelEvent('flow-step-viewed', {
      ...eventMetadata,
      parsedError: { componentState: stateInfo },
    });
  }, [
    loading,
    properties,
    steps,
    currentStep,
    currentStepFormData,
    identityVerification?.status,
  ]);

  function onVerifyPress() {
    startVerification({
      inquiryId: identityVerification?.providerVerificationId,
      sessionToken: identityVerification?.sessionToken,
      onComplete: () => refetchIdentityVerification(),
    });
  }

  async function onNextPress() {
    setLoading(true);

    try {
      trackMixpanelEvent('flow-step-completed', eventMetadata);

      await handleSaveAndNext({});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      trackError(error, 'onNextPress - handleSaveAndNext failed');
    }
  }

  const isCompleted =
    identityVerification?.status === 'Passed' ||
    identityVerification?.status === 'RequiresManualReview';

  const submitText = isCompleted ? 'Next' : 'Verify My Identity';
  const submitCallback = isCompleted ? onNextPress : onVerifyPress;

  function getBannerStep() {
    const nextLevelProfileIsCompleted = stepsByGate?.[
      STEP_GATE_NAMES.NextLevelProfile
    ]
      ?.filter(isStepUnHidden)
      ?.every(isStepCompleted);

    if (nextLevelProfileIsCompleted) {
      return <UserIdentityBannerStep />;
    }

    return undefined;
  }

  if (!currentStepFormData && !loading) {
    trackMixpanelEvent('flow-error', {
      ...eventMetadata,
      parsedError: {
        context: 'currentStepFormData is null despite data being loaded',
        componentState: {
          loading,
          hasProperties: !!properties,
          hasSteps: !!steps,
          hasCurrentStep: !!currentStep,
          hasCurrentStepFormData: !!currentStepFormData,
          stepsLength: steps?.length,
          currentStepId: currentStep?.uniqueId,
        },
      },
    });

    return null;
  }

  const isLoading =
    !hasFetchedIdentityVerification ||
    isLoadingIdentityVerification ||
    isLoadingPersona ||
    isVerifying ||
    isWaitingSafeDelay;

  return (
    <StepFormLayout
      title={USER_IDENTITY_STRINGS.title}
      subTitle={USER_IDENTITY_STRINGS.subtitle}
      bannerStep={getBannerStep()}
      showUnitAddress={false}
      onSubmit={submitCallback}
      getForm={() => (
        <div>
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white-translucent">
              <div className="mx-auto">
                <IconSpinner width={40} />
              </div>
            </div>
          )}
          <div className="mb-2xl">
            <IdentityVerificationBanner
              verificationStatus={identityVerification?.status}
            />
          </div>
        </div>
      )}
      ctaProps={{ label: submitText, disabled: isLoading }}
      disableProgressBar
    />
  );
}
